import type {
  AddVideoItemsToMainTrackAction, DeleteBoxAction, InsertMainTrackObjectsAction, MoveMainTrackBoxAction,
  SetInPointForSourceAction, SetOutPointForSourceAction,
} from '../private/actions/timelineActions';
import {
  ERR_ABORTABLE_OP_IN_PROGRESS, ERR_BOX_MISMATCH, ERR_IN_AFTER_OUT, ERR_ITEM_MISMATCH, ERR_NOT_MAIN_TRACK_BOX,
  ERR_OUT_PAST_DURATION, ERR_UNKNOWN_BOX, ERR_UNKNOWN_ITEM, OK,
  WARN_CLAMPED_TO_MAX, WARN_CLAMPED_TO_MIN,
} from './statusCodes';
import type { TimelinePublicOps } from './timelinePublicOps';
import type { Fraction } from '@mimir/common/util/fractionType';
import { addFractions, compareFractions, copyFrac, frac, greaterThanFraction, lessThanFraction, subtractFractions } from '@mimir/common/util/integerMath';
import { hasDuration } from '../private/timelineModel';
import type { ModelVideoTrackBox, TimelineModel } from '../private/timelineModel';
import { TimelineInternalHelper } from '../private/timelineInternalHelper';
import { ActionController } from '../private/actions/actionController';
import type { TimelineStateUpdaterOps } from './timelineStateUpdaterOps';
import { TimelineModelUpdater } from '../private/timelineModelUpdater';
import { CompositeTimelineStateUpdater } from '../private/compositeTimelineStateUpdater';
import type {
  InsertMainTrackObjectsActionAbortable, MoveMainTrackBoxActionAbortable,
  SetInPointForSourceActionAbortable, SetOutPointForSourceActionAbortable,
} from '../private/abortableOp';
import { findInsertTargetIndex, findMoveTargetIndex, findMoveTargetIndexForBoxGroup } from '../private/utils/findMoveOrInsertTargetIndex';
import type { VideoItemToInsert } from './types';
import { insertMainTrackObjects } from '../private/actions/ops/insertVideoItemsInMainTrack';
import { invariant } from '@mimir/common/util/invariant';


// Hardcoded for now using the same value as the existing
// timeline implementation. Down the line, this value should
// be dynamically adjusted to be a single frame of the
// timeline's target framerate
const MINIMUM_BOX_DURATION = frac(1, 20);


/**
 * This helper class is responsible for handling timeline
 * operations, typically from the UI code. Its role is to sanitize
 * the requests and to use TimelineInternalOps operations to convert
 * requests into actual modifications on the internal timeline state.
 * These modifications are propagated to the UI and rendering code
 * via the TimelineStateUpdaterOps objects passed to the constructor.
 *
 * For now, we always starts with an empty timeline that has
 * to be populated by applying operations. Support for saving
 * and loading timelines will come later.
 */
export class TimelineHelper implements TimelinePublicOps {

  private readonly timelineModel: TimelineModel;
  private readonly actionController: ActionController;
  private readonly compositeTimelineStateUpdater: TimelineStateUpdaterOps;
  private readonly timelineInternalHelper: TimelineInternalHelper;

  constructor(nAudioTracks: number, stateUpdaters: TimelineStateUpdaterOps[]) {
    this.timelineModel = {
      nextUiId: 0,
      mimirItemRefs: {},
      mimirItemRefCounts: {},
      sourceRefs: {},
      trackBoxesById: {},
      trackBoxesBySourceId: {},
      videoTracks: [],
      audioTracks: [],
    };
    const timelineModelUpdater = new TimelineModelUpdater(this.timelineModel);
    this.compositeTimelineStateUpdater = new CompositeTimelineStateUpdater([timelineModelUpdater, ...stateUpdaters]);
    this.timelineInternalHelper = new TimelineInternalHelper(this.timelineModel, this.compositeTimelineStateUpdater);
    this.actionController = new ActionController(this.timelineInternalHelper);

    this.compositeTimelineStateUpdater.addVideoTrack();
    for (let i = 0; i < nAudioTracks; i++) {
      this.compositeTimelineStateUpdater.addAudioTrack();
    }
  }


  /**
   * This method is only meant to be used from unit tests
   * that need to check the current state of the undo stack.
   */
  getLastActionIndex(): number {
    return this.actionController.getLastActionIndex();
  }


  abort(): void {
    this.actionController.abort();
  }


  completeCurrentOperation(): void {
    this.actionController.completeCurrentOperation();
  }


  undo() {
    this.actionController.undo();
  }


  redo() {
    this.actionController.redo();
  }


  updateItemDuration(itemId: string, durationInSeconds: Fraction) {
    const existingRef = this.timelineModel.mimirItemRefs[itemId];
    if (!existingRef) {
      return ERR_UNKNOWN_ITEM;
    }

    if (!hasDuration(existingRef)) {
      return ERR_ITEM_MISMATCH;
    }

    if (lessThanFraction(durationInSeconds, existingRef.durationInSeconds)) {
      return WARN_CLAMPED_TO_MAX;
    }

    existingRef.durationInSeconds = { ...durationInSeconds };
    return OK;
  }


  addVideoItemsToMainTrack(
    videoItemsToInsert: VideoItemToInsert[],
  ) {
    if (this.actionController.getCurrentAbortableOp() !== null) {
      return ERR_ABORTABLE_OP_IN_PROGRESS;
    }

    // Let's sanity-check all items to insert
    for (const videoItem of videoItemsToInsert) {
      const itemRef = this.timelineModel.mimirItemRefs[videoItem.itemId];
      if (itemRef && (itemRef.type !== 'video' || itemRef.nAudioChannels !== videoItem.nAudioChannels)) {
        return ERR_ITEM_MISMATCH;
      }

      if (!lessThanFraction(videoItem.inPointInSeconds, videoItem.outPointInSeconds)) {
        return ERR_IN_AFTER_OUT;
      }

      if (compareFractions(videoItem.outPointInSeconds, videoItem.durationInSeconds) > 0) {
        return ERR_OUT_PAST_DURATION;
      }
    }

    const action: AddVideoItemsToMainTrackAction = {
      type: 'AddVideoItemsToMainTrackAction',
      videoItems: videoItemsToInsert,
    };
    this.actionController.addAction(action);
    return OK;
  }


  tryUpdateVideoTrackBoxOutPoint(videoTrackBoxId: string, outPointInSeconds: Fraction) {
    const abortableOpId = `${videoTrackBoxId}`;
    const currentAbortableOp = this.actionController.getCurrentAbortableOp();
    if (currentAbortableOp !== null
        && (currentAbortableOp.opId !== abortableOpId || currentAbortableOp.type !== 'SetOutPointForSourceActionAbortable')) {
      return ERR_ABORTABLE_OP_IN_PROGRESS;
    }
    const box = this.timelineModel.trackBoxesById[videoTrackBoxId];
    if (box === undefined) {
      return ERR_UNKNOWN_BOX;
    }

    if (box.type !== 'video') {
      return ERR_BOX_MISMATCH;
    }

    const inPointPlusMinimumDuration = addFractions(box.inPointInSeconds, MINIMUM_BOX_DURATION);
    let newValue: Fraction;
    let ret: OK | WARN_CLAMPED_TO_MIN | WARN_CLAMPED_TO_MAX;

    if (lessThanFraction(outPointInSeconds, inPointPlusMinimumDuration)) {
      newValue = inPointPlusMinimumDuration;
      ret = WARN_CLAMPED_TO_MIN;
    } else if (lessThanFraction(box.sourceRef.itemRef.durationInSeconds, outPointInSeconds)) {
      newValue = copyFrac(box.sourceRef.itemRef.durationInSeconds);
      ret = WARN_CLAMPED_TO_MAX;
    } else {
      newValue = copyFrac(outPointInSeconds);
      ret = OK;
    }

    const oldOutPointInSeconds = copyFrac(box.outPointInSeconds);
    const sourcesDelta = subtractFractions(newValue, box.outPointInSeconds);

    const undoFn = this.timelineInternalHelper.setOutPointOfTrackBoxGroup(box.sourceRef.srcId, newValue, sourcesDelta);
    if (currentAbortableOp === null) {
      const action: SetOutPointForSourceAction = {
        type: 'SetOutPointForSourceAction',
        sourceId: box.sourceRef.srcId,
        sourcesDelta,
        newOutPointInSeconds: newValue,
      };
      const abortableOp: SetOutPointForSourceActionAbortable = {
        type: 'SetOutPointForSourceActionAbortable',
        opId: abortableOpId,
        abort: undoFn,
        action,
        oldOutPointInSeconds,
      };
      this.actionController.setAbortableAction(abortableOp);
    } else {
      currentAbortableOp.action.newOutPointInSeconds = newValue;
      currentAbortableOp.action.sourcesDelta = subtractFractions(newValue, currentAbortableOp.oldOutPointInSeconds);
    }

    return ret;
  }


  tryUpdateVideoTrackBoxInPoint(videoTrackBoxId: string, inPointInSeconds: Fraction) {
    const abortableOpId = `${videoTrackBoxId}`;
    const currentAbortableOp = this.actionController.getCurrentAbortableOp();
    if (currentAbortableOp !== null
        && (currentAbortableOp.opId !== abortableOpId || currentAbortableOp.type !== 'SetInPointForSourceActionAbortable')) {
      return ERR_ABORTABLE_OP_IN_PROGRESS;
    }
    const box = this.timelineModel.trackBoxesById[videoTrackBoxId];
    if (box === undefined) {
      return ERR_UNKNOWN_BOX;
    }

    if (box.type !== 'video') {
      return ERR_BOX_MISMATCH;
    }

    const outPointMinusMinimumDuration = subtractFractions(box.outPointInSeconds, MINIMUM_BOX_DURATION);

    let newValue: Fraction;
    let ret: OK | WARN_CLAMPED_TO_MIN | WARN_CLAMPED_TO_MAX;

    if (greaterThanFraction(inPointInSeconds, outPointMinusMinimumDuration)) {
      newValue = outPointMinusMinimumDuration;
      ret = WARN_CLAMPED_TO_MAX;
    } else if (lessThanFraction(inPointInSeconds, { numerator: 0, denominator: 1 })) {
      newValue = { numerator: 0, denominator: 1 };
      ret = WARN_CLAMPED_TO_MIN;
    } else {
      newValue = copyFrac(inPointInSeconds);
      ret = OK;
    }

    const oldInPointInSeconds = copyFrac(box.inPointInSeconds);
    const sourcesDelta = subtractFractions(box.inPointInSeconds, newValue);

    const undoFn = this.timelineInternalHelper.setInPointOfTrackBoxGroup(box.sourceRef.srcId, newValue, sourcesDelta);
    if (currentAbortableOp === null) {
      const action: SetInPointForSourceAction = {
        type: 'SetInPointForSourceAction',
        sourceId: box.sourceRef.srcId,
        sourcesDelta,
        newInPointInSeconds: newValue,
      };
      const abortableOp: SetInPointForSourceActionAbortable = {
        type: 'SetInPointForSourceActionAbortable',
        opId: abortableOpId,
        abort: undoFn,
        action,
        oldInPointInSeconds,
      };
      this.actionController.setAbortableAction(abortableOp);
    } else {
      currentAbortableOp.action.newInPointInSeconds = newValue;
      currentAbortableOp.action.sourcesDelta = subtractFractions(currentAbortableOp.oldInPointInSeconds, newValue);
    }

    return ret;
  }


  tryMoveMainTrackBox(mainTrackBoxId: string, posInSeconds: Fraction) {
    const abortableOpId = `${mainTrackBoxId}`;
    const currentAbortableOp = this.actionController.getCurrentAbortableOp();
    if (currentAbortableOp !== null
        && (currentAbortableOp.opId !== abortableOpId || currentAbortableOp.type !== 'MoveMainTrackBoxActionAbortable')) {
      return ERR_ABORTABLE_OP_IN_PROGRESS;
    }
    const box = this.timelineModel.trackBoxesById[mainTrackBoxId];
    if (box === undefined) {
      return ERR_UNKNOWN_BOX;
    }

    const sourceIndex = this.timelineModel.videoTracks[0].findIndex(b => b.boxId === mainTrackBoxId);
    if (sourceIndex === -1) {
      return ERR_NOT_MAIN_TRACK_BOX;
    }

    const targetIndex = findMoveTargetIndex(this.timelineModel.videoTracks[0], sourceIndex, posInSeconds);

    const originalSourceIndex = currentAbortableOp?.oldIndex ?? sourceIndex;
    const action: MoveMainTrackBoxAction | null = (targetIndex === originalSourceIndex) ? null : {
      type: 'MoveMainTrackBoxAction',
      sourceIndex: originalSourceIndex,
      targetIndex,
    };

    const undoFn = this.timelineInternalHelper.moveMainVideoTrackBox(sourceIndex, targetIndex);
    const sourceRef = this.timelineModel.trackBoxesById[mainTrackBoxId].sourceRef;

    if (currentAbortableOp === null) {
      this.compositeTimelineStateUpdater.setSourceDragged(sourceRef, true);
      const abortableOp: MoveMainTrackBoxActionAbortable = {
        type: 'MoveMainTrackBoxActionAbortable',
        opId: abortableOpId,
        oldIndex: sourceIndex,
        abort: () => {
          undoFn();
          this.compositeTimelineStateUpdater.setSourceDragged(sourceRef, false);
        },
        action,
      };
      this.actionController.setAbortableAction(abortableOp);
    } else {
      currentAbortableOp.abort = () => {
        this.timelineInternalHelper.moveMainVideoTrackBox(targetIndex, currentAbortableOp.oldIndex);
        this.compositeTimelineStateUpdater.setSourceDragged(sourceRef, false);
      };
      currentAbortableOp.action = action;
    }
    return OK;
  }


  tryInsertMainTrackObjects(
    videoItemsToInsert: VideoItemToInsert[],
    posInSeconds: Fraction,
  ) {
    const abortableOpId = JSON.stringify(videoItemsToInsert);
    const currentAbortableOp = this.actionController.getCurrentAbortableOp();
    if (currentAbortableOp !== null
        && (currentAbortableOp.opId !== abortableOpId || currentAbortableOp.type !== 'InsertMainTrackObjectsActionAbortable')) {
      return ERR_ABORTABLE_OP_IN_PROGRESS;
    }

    if (currentAbortableOp === null) {
      const targetIndex = findInsertTargetIndex(this.timelineModel.videoTracks[0], posInSeconds);
      const action: InsertMainTrackObjectsAction = {
        type: 'InsertMainTrackObjectsAction',
        videoItemsToInsert,
        targetIndex,
      };
      insertMainTrackObjects(this.timelineInternalHelper, action);
      const undoInsertion = action.undo;
      invariant(undoInsertion !== undefined, 'undo should be set after insertMainTrackObjects()');

      const boxes: ModelVideoTrackBox[] = [];
      for (let i = targetIndex; i < targetIndex + videoItemsToInsert.length; i++) {
        const box = this.timelineModel.videoTracks[0][i];
        boxes.push(box);
        this.compositeTimelineStateUpdater.setSourceDragged(box.sourceRef, true);
      }

      const abortableOp: InsertMainTrackObjectsActionAbortable = {
        type: 'InsertMainTrackObjectsActionAbortable',
        opId: abortableOpId,
        action,
        boxes,
        abort: undoInsertion,
      };
      this.actionController.setAbortableAction(abortableOp);
    } else {
      // If we already have an action, we need to update its target index
      // and move the boxes to this index
      const firstBoxId = currentAbortableOp.boxes[0].boxId;
      const firstBoxIndex = this.timelineModel.videoTracks[0].findIndex(b => b.boxId === firstBoxId);
      invariant(firstBoxIndex !== -1, () => `Could not find main track box ${firstBoxId}`);

      const targetIndex = findMoveTargetIndexForBoxGroup(this.timelineModel.videoTracks[0], firstBoxIndex, videoItemsToInsert.length, posInSeconds);
      this.timelineInternalHelper.moveMainVideoTrackBoxes(videoItemsToInsert.length, firstBoxIndex, targetIndex);
      currentAbortableOp.action.targetIndex = targetIndex;
    }

    return OK;
  }


  deleteTrackBox(boxId: string) {
    const currentAbortableOp = this.actionController.getCurrentAbortableOp();
    if (currentAbortableOp !== null) {
      return ERR_ABORTABLE_OP_IN_PROGRESS;
    }

    const box = this.timelineModel.trackBoxesById[boxId];
    if (!box) {
      return ERR_UNKNOWN_BOX;
    }

    const action: DeleteBoxAction = {
      type: 'DeleteBoxAction',
      box,
    };
    this.actionController.addAction(action);
    return OK;
  }

}
